import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";

import Icon from "../../components/Icon";
import { ApiError } from "../../api/client";
import { useCreateEntry } from "../../api/mutations";
import { useOdometerEntries } from "../../api/queries";
import type {
  CarSummary,
  CreateEntryInput,
  FormOptionsResponse,
  FuelStation,
} from "../../api/types";
import {
  FUEL_TYPES_BY_CATEGORY,
  fuelTypeLabel,
} from "../garage/carFormConstants";
import FuelStationFormModal from "../fuel-stations/FuelStationFormModal";
import {
  formatMileage,
  odometerBounds,
  parseMileage,
} from "../../lib/odometer";
import { useDirtyForm } from "./useDirtyForm";

type FullTank = "true" | "false" | "unknown";
type PriceField = "liters" | "amount_eur" | "price_per_liter_eur";

const PRICE_FIELDS: PriceField[] = [
  "liters",
  "amount_eur",
  "price_per_liter_eur",
];

interface FormState {
  car_id: string;
  station_choice: string;
  fuel_station_id: string;
  odometer_km: string;
  liters: string;
  amount_eur: string;
  price_per_liter_eur: string;
  fuel_type: string;
  is_full_tank: FullTank;
  entry_date: string;
  notes: string;
  [key: string]: string;
}

export interface EntryFormProps {
  formOptions: FormOptionsResponse;
  cars: CarSummary[];
  activeCarId: number | null;
  fuelStations: FuelStation[];
  recentFuelStationId: number | null;
  onDirtyChange: (isDirty: boolean) => void;
  onSaved: () => void;
}

function parseDecimal(raw: string): number | null {
  const cleaned = raw.trim().replace(",", ".");
  if (!cleaned) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

function formatDecimal(value: number, decimals: number): string {
  const fixed = value.toFixed(decimals);
  return fixed.includes(".") ? fixed.replace(/\.?0+$/, "") : fixed;
}

function stationValue(station: FuelStation): string {
  return `station:${station.id}`;
}

function stationIdFromValue(value: string): number | null {
  if (!value.startsWith("station:")) return null;
  const id = Number(value.slice("station:".length));
  return Number.isFinite(id) ? id : null;
}

function calculatePriceField(
  target: PriceField,
  values: FormState,
): string {
  const liters = parseDecimal(values.liters);
  const amount = parseDecimal(values.amount_eur);
  const unit = parseDecimal(values.price_per_liter_eur);

  if (target === "price_per_liter_eur" && liters && amount) {
    return formatDecimal(amount / liters, 3);
  }
  if (target === "amount_eur" && liters && unit) {
    return formatDecimal(liters * unit, 2);
  }
  if (target === "liters" && amount && unit) {
    return formatDecimal(amount / unit, 2);
  }
  return "";
}

function groupStations(stations: FuelStation[]): [string, FuelStation[]][] {
  const grouped = new Map<string, FuelStation[]>();
  for (const station of stations) {
    const list = grouped.get(station.country) ?? [];
    list.push(station);
    grouped.set(station.country, list);
  }
  return Array.from(grouped.entries());
}

export default function EntryForm({
  formOptions,
  cars,
  activeCarId,
  fuelStations,
  recentFuelStationId,
  onDirtyChange,
  onSaved,
}: EntryFormProps) {
  const [localStations, setLocalStations] = useState<FuelStation[]>([]);
  const allStations = useMemo(() => {
    const byId = new Map<number, FuelStation>();
    for (const station of fuelStations) byId.set(station.id, station);
    for (const station of localStations) byId.set(station.id, station);
    return Array.from(byId.values()).sort((a, b) =>
      `${a.country}\u0000${a.city}\u0000${a.name}`.localeCompare(
        `${b.country}\u0000${b.city}\u0000${b.name}`,
      ),
    );
  }, [fuelStations, localStations]);

  const initialState = useMemo<FormState>(() => {
    const selectedCar =
      cars.find((car) => car.id === activeCarId) ?? cars[0] ?? null;
    const recentStation =
      fuelStations.find((station) => station.id === recentFuelStationId) ?? null;
    const fuelTypes = selectedCar
      ? FUEL_TYPES_BY_CATEGORY[selectedCar.fuel_category]
      : formOptions.fuel_types;
    const defaultFuelType =
      selectedCar?.preferred_fuel_type && fuelTypes.includes(selectedCar.preferred_fuel_type)
        ? selectedCar.preferred_fuel_type
        : fuelTypes[0] ?? "";

    return {
      car_id: selectedCar ? String(selectedCar.id) : "",
      station_choice: recentStation ? stationValue(recentStation) : "unknown",
      fuel_station_id: recentStation ? String(recentStation.id) : "",
      odometer_km: formatMileage(selectedCar?.current_odometer_km ?? null),
      liters: "",
      amount_eur: "",
      price_per_liter_eur: "",
      fuel_type: defaultFuelType,
      is_full_tank: "true",
      entry_date: formOptions.now_local,
      notes: "",
    };
  }, [
    activeCarId,
    cars,
    fuelStations,
    formOptions.default_country,
    formOptions.fuel_types,
    formOptions.now_local,
    recentFuelStationId,
  ]);

  const [values, setValues] = useState<FormState>(initialState);
  const [manualPriceFields, setManualPriceFields] = useState<PriceField[]>([]);
  const [manualMileage, setManualMileage] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<{ text: string; variant?: "error" } | null>(
    null,
  );
  const [photoName, setPhotoName] = useState<string | null>(null);
  const [isStationModalOpen, setIsStationModalOpen] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const { snapshot, isDirty } = useDirtyForm<FormState>();
  const createEntry = useCreateEntry();
  const pending = createEntry.isPending;

  const selectedCar = useMemo(
    () => cars.find((car) => String(car.id) === values.car_id) ?? null,
    [cars, values.car_id],
  );
  const odometerQuery = useOdometerEntries(selectedCar?.id ?? null);
  const odometerEntries = odometerQuery.data?.entries ?? [];
  const mileageBounds = useMemo(
    () => odometerBounds(odometerEntries, values.entry_date),
    [odometerEntries, values.entry_date],
  );
  const enteredMileage = parseMileage(values.odometer_km);
  const mileageError =
    values.odometer_km.trim() && enteredMileage === null
      ? "Must be a valid number."
      : enteredMileage !== null && enteredMileage <= 0
        ? "Must be greater than 0."
        : enteredMileage !== null &&
            mileageBounds.previous !== null &&
            enteredMileage < mileageBounds.previous.odometer_km
          ? `Must be at least ${formatMileage(
              mileageBounds.previous.odometer_km,
            )} km.`
          : enteredMileage !== null &&
              mileageBounds.next !== null &&
              enteredMileage > mileageBounds.next.odometer_km
            ? `Must be at most ${formatMileage(
                mileageBounds.next.odometer_km,
              )} km.`
            : "";
  const mileageHintParts = [
    mileageBounds.previous
      ? `min ${formatMileage(mileageBounds.previous.odometer_km)} km`
      : null,
    mileageBounds.next
      ? `max ${formatMileage(mileageBounds.next.odometer_km)} km`
      : null,
  ].filter(Boolean);
  const dateError =
    values.entry_date.trim() && Number.isNaN(Date.parse(values.entry_date))
      ? "Must be a valid date/time."
      : "";

  const fuelTypeOptions = selectedCar
    ? FUEL_TYPES_BY_CATEGORY[selectedCar.fuel_category]
    : formOptions.fuel_types;

  const calculatedPriceField =
    manualPriceFields.length === 2
      ? PRICE_FIELDS.find((field) => !manualPriceFields.includes(field)) ?? null
      : null;

  useEffect(() => {
    snapshot(initialState);
  }, [initialState, snapshot]);

  useEffect(() => {
    onDirtyChange(isDirty(values));
  }, [values, isDirty, onDirtyChange]);

  useEffect(() => {
    if (!fuelTypeOptions.includes(values.fuel_type)) {
      setValues((prev) => ({
        ...prev,
        fuel_type: selectedCar?.preferred_fuel_type ?? fuelTypeOptions[0] ?? "",
      }));
    }
  }, [fuelTypeOptions, selectedCar?.preferred_fuel_type, values.fuel_type]);

  useEffect(() => {
    if (manualMileage || !odometerQuery.data) return;
    setValues((prev) => ({
      ...prev,
      odometer_km: formatMileage(mileageBounds.previous?.odometer_km),
    }));
  }, [manualMileage, mileageBounds.previous?.odometer_km, odometerQuery.data]);

  const setField = useCallback(<K extends keyof FormState>(name: K, value: FormState[K]) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const updatePriceFields = useCallback((field: PriceField, raw: string) => {
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    let nextManual = manualPriceFields.filter((name) => name !== field);
    if (raw.trim()) nextManual = [...nextManual, field];
    if (nextManual.length > 2) nextManual = nextManual.slice(-2);
    setManualPriceFields(nextManual);

    setValues((prev) => {
      const next = { ...prev, [field]: raw };
      const automatic = PRICE_FIELDS.filter((name) => !nextManual.includes(name));
      if (nextManual.length === 2) {
        const target = automatic[0];
        next[target] = calculatePriceField(target, next);
      } else {
        for (const target of automatic) {
          if (target !== field) next[target] = "";
        }
      }
      return next;
    });
  }, [manualPriceFields]);

  const enforceDecimals = useCallback((field: PriceField) => {
    if (calculatedPriceField === field) return;
    const decimals = field === "price_per_liter_eur" ? 3 : 2;
    const current = parseDecimal(values[field]);
    if (current === null) return;
    updatePriceFields(field, formatDecimal(current, decimals));
  }, [calculatedPriceField, updatePriceFields, values]);

  const handleVehicleChange = (carId: string) => {
    const car = cars.find((candidate) => String(candidate.id) === carId) ?? null;
    const fuelTypes = car ? FUEL_TYPES_BY_CATEGORY[car.fuel_category] : formOptions.fuel_types;
    setValues((prev) => ({
      ...prev,
      car_id: carId,
      odometer_km: formatMileage(car?.current_odometer_km ?? null),
      fuel_type:
        car?.preferred_fuel_type && fuelTypes.includes(car.preferred_fuel_type)
          ? car.preferred_fuel_type
          : fuelTypes[0] ?? prev.fuel_type,
    }));
    setManualMileage(false);
    setFieldErrors((prev) => ({ ...prev, odometer_km: "" }));
  };

  const handleStationChoice = (choice: string) => {
    const stationId = stationIdFromValue(choice);
    if (stationId !== null) {
      const station = allStations.find((item) => item.id === stationId);
      if (!station) return;
      setValues((prev) => ({
        ...prev,
        station_choice: choice,
        fuel_station_id: String(station.id),
      }));
      return;
    }

    if (choice === "unknown") {
      setValues((prev) => ({
        ...prev,
        station_choice: "unknown",
        fuel_station_id: "",
      }));
      return;
    }

    setValues((prev) => ({
      ...prev,
      station_choice: "unknown",
      fuel_station_id: "",
    }));
  };

  const handleStationCreated = (station: FuelStation) => {
    setLocalStations((prev) => [...prev, station]);
    setValues((prev) => ({
      ...prev,
      station_choice: stationValue(station),
      fuel_station_id: String(station.id),
    }));
    setFieldErrors((prev) => ({ ...prev, fuel_station_id: "" }));
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFieldErrors({});

    if (dateError) {
      setFieldErrors({ entry_date: dateError });
      setStatus({ text: "Please fix the highlighted fields.", variant: "error" });
      return;
    }

    if (enteredMileage === null || mileageError) {
      setFieldErrors({ odometer_km: mileageError || "Required." });
      setStatus({ text: "Please fix the highlighted fields.", variant: "error" });
      return;
    }

    setStatus({ text: "Saving..." });
    const selectedStationId = stationIdFromValue(values.station_choice);
    const selectedStation =
      selectedStationId === null
        ? null
        : allStations.find((station) => station.id === selectedStationId) ?? null;
    if (values.station_choice !== "unknown" && selectedStation === null) {
      setFieldErrors({ fuel_station_id: "Select a saved station or Unknown." });
      setStatus({ text: "Please fix the highlighted fields.", variant: "error" });
      return;
    }

    const input: CreateEntryInput = {
      car_id: values.car_id || undefined,
      fuel_station_id: selectedStation ? String(selectedStation.id) : undefined,
      station_name: selectedStation?.name ?? "Unknown",
      city: selectedStation?.city ?? "Unknown",
      country: selectedStation?.country ?? formOptions.default_country,
      liters: String(parseDecimal(values.liters) ?? ""),
      amount_eur: String(parseDecimal(values.amount_eur) ?? ""),
      odometer_km: String(parseMileage(values.odometer_km) ?? ""),
      entry_date: values.entry_date,
      fuel_type: values.fuel_type,
      is_full_tank: values.is_full_tank,
      notes: values.notes,
    };

    try {
      const result = await createEntry.mutateAsync(input);
      const successMessage =
        result.warnings && result.warnings.length
          ? `Saved - ${result.warnings.join(" ")}`
          : "Saved.";
      setStatus({ text: successMessage });
      window.setTimeout(() => {
        onSaved();
      }, 350);
    } catch (err) {
      if (err instanceof ApiError && err.errors) {
        setFieldErrors(err.errors);
        setStatus({ text: "Please fix the highlighted fields.", variant: "error" });
      } else {
        const message = err instanceof Error ? err.message : String(err);
        setStatus({ text: `Could not save: ${message}`, variant: "error" });
      }
    }
  };

  const onCameraClick = () => cameraInputRef.current?.click();

  const onCameraChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setStatus({ text: `Attached photo: ${file.name} (OCR coming soon)` });
      setPhotoName(file.name);
    }
  };

  return (
    <>
      <form className="entry-form" onSubmit={onSubmit} noValidate>
        <label className="field">
          <span className="field__label">Vehicle</span>
          <select
            className="field__input field__select"
            name="car_id"
            required
            value={values.car_id}
            onChange={(e) => handleVehicleChange(e.target.value)}
          >
            {cars.map((car) => (
              <option key={car.id} value={car.id}>
                {car.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Mileage (km)</span>
          <input
            className="field__input"
            type="text"
            name="odometer_km"
            inputMode="decimal"
            autoComplete="off"
            required
            value={values.odometer_km}
            onChange={(e) => {
              setField("odometer_km", e.target.value);
              setManualMileage(true);
              setFieldErrors((prev) => ({ ...prev, odometer_km: "" }));
            }}
          />
          {mileageHintParts.length > 0 ? (
            <span className="field__help">{mileageHintParts.join(" / ")}</span>
          ) : null}
          <span className="field__error" data-error-for="odometer_km">
            {fieldErrors.odometer_km || mileageError}
          </span>
        </label>

        <div className="field field--with-action">
          <span className="field__label">Saved station</span>
          <div className="field-action-row">
            <select
              className="field__input field__select"
              name="fuel_station_choice"
              aria-label="Saved station"
              value={values.station_choice}
              onChange={(e) => handleStationChoice(e.target.value)}
            >
              <option value="unknown">Unknown</option>
              {groupStations(allStations).map(([country, stations]) => (
                <optgroup key={country} label={country}>
                  {stations.map((station) => (
                    <option key={station.id} value={stationValue(station)}>
                      {station.name} - {station.city}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <button
              className="button button--ghost button--icon"
              type="button"
              aria-label="Add fuel station"
              onClick={() => setIsStationModalOpen(true)}
            >
              <Icon name="plus" />
            </button>
          </div>
          <span className="field__error" data-error-for="fuel_station_id">
            {fieldErrors.fuel_station_id ?? ""}
          </span>
        </div>

        <div className="field-row field-row--numbers">
          <label className="field field--grow">
            <span className="field__label">Liters</span>
            <input
              className="field__input"
              data-readonly={calculatedPriceField === "liters" ? "true" : undefined}
              type="text"
              name="liters"
              inputMode="decimal"
              autoComplete="off"
              required
              readOnly={calculatedPriceField === "liters"}
              value={values.liters}
              onChange={(e) => updatePriceFields("liters", e.target.value)}
              onBlur={() => enforceDecimals("liters")}
            />
            <span className="field__error" data-error-for="liters">
              {fieldErrors.liters ?? ""}
            </span>
          </label>
          <label className="field field--grow">
            <span className="field__label">Total price (€)</span>
            <input
              className="field__input"
              data-readonly={calculatedPriceField === "amount_eur" ? "true" : undefined}
              type="text"
              name="amount_eur"
              inputMode="decimal"
              autoComplete="off"
              required
              readOnly={calculatedPriceField === "amount_eur"}
              value={values.amount_eur}
              onChange={(e) => updatePriceFields("amount_eur", e.target.value)}
              onBlur={() => enforceDecimals("amount_eur")}
            />
            <span className="field__error" data-error-for="amount_eur">
              {fieldErrors.amount_eur ?? ""}
            </span>
          </label>
          <label className="field field--grow">
            <span className="field__label">€/liter</span>
            <input
              className="field__input"
              data-readonly={
                calculatedPriceField === "price_per_liter_eur" ? "true" : undefined
              }
              type="text"
              name="price_per_liter_eur"
              inputMode="decimal"
              autoComplete="off"
              readOnly={calculatedPriceField === "price_per_liter_eur"}
              value={values.price_per_liter_eur}
              onChange={(e) => updatePriceFields("price_per_liter_eur", e.target.value)}
              onBlur={() => enforceDecimals("price_per_liter_eur")}
            />
          </label>
        </div>

        <div className="field-row field-row--meta">
          <label className="field field--grow">
            <span className="field__label">Fuel type</span>
            <select
              className="field__input field__select"
              name="fuel_type"
              required
              value={values.fuel_type}
              onChange={(e) => setField("fuel_type", e.target.value)}
            >
              {fuelTypeOptions.map((ft) => (
                <option key={ft} value={ft}>
                  {fuelTypeLabel(ft)}
                </option>
              ))}
            </select>
          </label>
          <label className="field field--grow">
            <span className="field__label">Full tank</span>
            <select
              className="field__input field__select"
              name="is_full_tank"
              value={values.is_full_tank}
              onChange={(e) => setField("is_full_tank", e.target.value as FullTank)}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
        </div>

        <label className="field">
          <span className="field__label">Date &amp; time</span>
          <input
            className="field__input"
            type="datetime-local"
            name="entry_date"
            required
            value={values.entry_date}
            onChange={(e) => {
              setField("entry_date", e.target.value);
              setManualMileage(false);
              setFieldErrors((prev) => ({ ...prev, entry_date: "" }));
            }}
          />
          <span className="field__error" data-error-for="entry_date">
            {fieldErrors.entry_date || dateError}
          </span>
        </label>

        <label className="field">
          <span className="field__label">Notes (optional)</span>
          <input
            className="field__input"
            type="text"
            name="notes"
            autoComplete="off"
            value={values.notes}
            onChange={(e) => setField("notes", e.target.value)}
          />
        </label>

        {status ? (
          <div
            className="form-status"
            data-form-status
            data-variant={status.variant}
            role={status.variant === "error" ? "alert" : "status"}
          >
            {status.text}
          </div>
        ) : null}

        <div className="form-actions">
          <button
            className="button button--camera"
            type="button"
            aria-label="Scan a receipt (not implemented yet)"
            onClick={onCameraClick}
          >
            <Icon name="camera" />
          </button>
          <button
            className="button button--success button--equal"
            type="submit"
            disabled={pending || Boolean(mileageError) || Boolean(dateError)}
          >
            Save
          </button>
        </div>

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={onCameraChange}
          data-photo-name={photoName ?? undefined}
        />
      </form>

      <FuelStationFormModal
        isOpen={isStationModalOpen}
        onClose={() => setIsStationModalOpen(false)}
        countries={formOptions.countries}
        defaultCountry={
          allStations.find((station) => stationValue(station) === values.station_choice)
            ?.country ?? formOptions.default_country
        }
        onCreated={handleStationCreated}
      />
    </>
  );
}
